import { Controller } from "@hotwired/stimulus"
import { leave, toggle } from "el-transition"

export default class extends Controller {
  // The "button" is the element that you click to toggle the dropdown.
  // The "menu" contains the dropdown menu items, this is typically a div.
  static targets = ["menu", "button"]
  // autoClose: "outside" keeps the menu open for in-menu clicks (checkbox
  // filters — Bootstrap's data-bs-auto-close="outside"). Default preserves
  // the original behavior: any click that isn't the button closes the menu
  // (menu links close on click, as site/manage nav expects).
  static values = { autoClose: { type: String, default: "" } }

  /*
  / Toggle the visiblity of the menu based on if it's shown or not. The name
  / is not shadowed because our custom toggle method would be this.toggle(),
  / not toggle() which is supplied by el-transition.
  /
  / el-transition will hide or show this element (our menu) and apply the
  / transition classes that are defined as data attributes in the HTML.
  */
  toggle() {
    const willOpen = this.menuTarget.classList.contains("hidden")
    toggle(this.menuTarget)
    this.#setExpanded(willOpen)
  }

  // We only want to hide the menu when we click anything except the button
  // or the menu itself. This lets us click anywhere outside of the dropdown
  // to hide it, while clicks inside the menu (checkboxes, links) keep it open
  // — matching Bootstrap's data-bs-auto-close="outside" behavior.
  hide(event) {
    const clickedButton = this.buttonTarget.contains(event.target)
    const clickedMenu = this.menuTarget.contains(event.target)
    const keepOpen =
      this.autoCloseValue === "outside" ? clickedButton || clickedMenu : clickedButton

    if (!keepOpen) {
      leave(this.menuTarget)
      this.#setExpanded(false)
    }
  }

  // Close without toggling — for Esc bindings (keydown.esc->dropdown#close),
  // so pressing Esc on a closed dropdown doesn't reopen it.
  close() {
    if (!this.menuTarget.classList.contains("hidden")) {
      leave(this.menuTarget)
      this.#setExpanded(false)
    }
  }

  #setExpanded(expanded) {
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", expanded.toString())
    }
  }
}
